/**
 * Minecart Switchyard: route cargo carts to the depot for their cargo by setting junction
 * switches before the run. Minecraft-*inspired*, with our own rules (shown to players):
 *
 * Rules (ruleset v1):
 *  - Every switch is set before Run and stays put. Every run starts from the same state.
 *  - Each cart appears on its loading spot at a fixed tick, then rolls one rail per tick.
 *  - Rails connect two sides; a crossing (+) goes straight on; a depot takes any cart in.
 *  - A switch has a trunk and two branches. From the trunk a cart takes the branch the switch
 *    points to. From either branch it always rolls on to the trunk (it pushes through).
 *  - A run fails when two carts enter one cell on the same tick, two carts swap places along one
 *    rail (head-on), a cart is loaded onto a cell another cart is entering, a cart runs off the
 *    rails, or a cart reaches the wrong depot. It wins when every cart is in its own depot.
 */
import { DX, DY, type Dir } from './redstone.ts';

export const MINECART_RULESET = 1;
export const MAX_CART_TICKS = 80;
export const MAX_YARD = 12;

export type Cargo = 'redstone' | 'lapis' | 'emerald';
export const CARGO: Cargo[] = ['redstone', 'lapis', 'emerald'];

export type Track =
  | { kind: 'rail'; ends: [Dir, Dir] }
  | { kind: 'cross' }
  /** `a` is where it points in the starting layout; thrown, it points to `b`. */
  | { kind: 'switch'; id: string; trunk: Dir; a: Dir; b: Dir }
  | { kind: 'depot'; cargo: Cargo };

export interface CartSpec {
  cargo: Cargo;
  x: number;
  y: number;
  dir: Dir;
  /** Tick it appears on its loading spot. */
  tick: number;
}

export interface MinecartLevel {
  id: string;
  version: number;
  title: string;
  goal: string;
  hints: string[];
  width: number;
  height: number;
  /** Key "x,y". */
  track: Record<string, Track>;
  carts: CartSpec[];
  /** Gold medal: solved changing at most this many switches from the starting layout. */
  goldChanges: number;
  /** A known solution (tests prove it wins with gold; never sent as a hint). */
  solution: SwitchBoard;
}

/** Switch id → thrown (points to its `b` branch). Missing means the starting layout. */
export interface SwitchBoard {
  switches: Record<string, boolean>;
}

export interface CartFrame {
  x: number;
  y: number;
  dir: Dir;
  state: 'waiting' | 'rolling' | 'arrived' | 'stopped';
}

export type Failure =
  | { kind: 'crash'; tick: number; carts: [number, number]; x: number; y: number }
  | { kind: 'swap'; tick: number; carts: [number, number] }
  | { kind: 'spawn'; tick: number; carts: [number, number]; x: number; y: number }
  | { kind: 'dead-end'; tick: number; cart: number; x: number; y: number }
  | { kind: 'wrong-depot'; tick: number; cart: number; depot: Cargo }
  | { kind: 'too-long'; tick: number };

export interface CartRun {
  ok: true;
  /** frames[t][cart]: where every cart is after tick t. */
  frames: CartFrame[][];
  outcome: 'win' | Failure;
  changes: number;
}

const key = (x: number, y: number) => `${x},${y}`;
const back = (d: Dir) => ((d + 2) % 4) as Dir;

type Enter = { dir: Dir } | { depot: Cargo } | null;

/** Where a cart heading `dir` goes after rolling into `t`. */
function enter(t: Track | undefined, dir: Dir, thrown: Record<string, boolean>): Enter {
  const from = back(dir);
  if (!t) return null;
  if (t.kind === 'depot') return { depot: t.cargo };
  if (t.kind === 'cross') return { dir };
  if (t.kind === 'rail') {
    if (from === t.ends[0]) return { dir: t.ends[1] };
    if (from === t.ends[1]) return { dir: t.ends[0] };
    return null;
  }
  if (from === t.trunk) return { dir: thrown[t.id] ? t.b : t.a };
  if (from === t.a || from === t.b) return { dir: t.trunk };
  return null;
}

export const switchIds = (level: MinecartLevel) =>
  Object.values(level.track).flatMap((t) => (t.kind === 'switch' ? [t.id] : []));

/** Runs the yard from its fixed start. Pure and deterministic. */
export function runCarts(
  level: MinecartLevel,
  board: SwitchBoard,
): CartRun | { ok: false; error: string } {
  if (level.width > MAX_YARD || level.height > MAX_YARD)
    return { ok: false, error: 'yard too big' };
  const ids = new Set(switchIds(level));
  for (const [id, v] of Object.entries(board.switches))
    if (!ids.has(id) || typeof v !== 'boolean') return { ok: false, error: 'unknown switch' };
  const changes = Object.values(board.switches).filter(Boolean).length;

  let carts: CartFrame[] = level.carts.map((c) => ({
    x: c.x,
    y: c.y,
    dir: c.dir,
    state: 'waiting',
  }));
  const frames: CartFrame[][] = [];
  for (let t = 0; t < MAX_CART_TICKS; t++) {
    const prev = carts;
    const fails: Failure[] = [];
    carts = prev.map((c, i) => {
      if (c.state === 'rolling') {
        const nx = c.x + DX[c.dir]!;
        const ny = c.y + DY[c.dir]!;
        const e = enter(level.track[key(nx, ny)], c.dir, board.switches);
        if (e && 'dir' in e) return { x: nx, y: ny, dir: e.dir, state: 'rolling' };
        if (e) {
          if (e.depot !== level.carts[i]!.cargo)
            fails.push({ kind: 'wrong-depot', tick: t, cart: i, depot: e.depot });
          return { x: nx, y: ny, dir: c.dir, state: 'arrived' };
        }
        fails.push({ kind: 'dead-end', tick: t, cart: i, x: nx, y: ny });
        return { ...c, state: 'stopped' };
      }
      if (c.state === 'waiting' && level.carts[i]!.tick === t) return { ...c, state: 'rolling' };
      return c;
    });
    // Collisions, among carts on the rails this tick (and ones that just rolled into a depot).
    const active = (i: number) =>
      carts[i]!.state === 'rolling' ||
      carts[i]!.state === 'stopped' ||
      (carts[i]!.state === 'arrived' && prev[i]!.state === 'rolling');
    for (let i = 0; i < carts.length; i++)
      for (let j = i + 1; j < carts.length; j++) {
        if (!active(i) || !active(j)) continue;
        const a = carts[i]!;
        const b = carts[j]!;
        const spawned = (k: number) => prev[k]!.state === 'waiting';
        if (a.x === b.x && a.y === b.y)
          fails.unshift({
            kind: spawned(i) || spawned(j) ? 'spawn' : 'crash',
            tick: t,
            carts: [i, j],
            x: a.x,
            y: a.y,
          });
        else if (
          !spawned(i) &&
          !spawned(j) &&
          a.x === prev[j]!.x &&
          a.y === prev[j]!.y &&
          b.x === prev[i]!.x &&
          b.y === prev[i]!.y
        )
          fails.unshift({ kind: 'swap', tick: t, carts: [i, j] });
      }
    frames.push(carts);
    if (fails.length) return { ok: true, frames, outcome: fails[0]!, changes };
    if (carts.every((c) => c.state === 'arrived'))
      return { ok: true, frames, outcome: 'win', changes };
  }
  return { ok: true, frames, outcome: { kind: 'too-long', tick: MAX_CART_TICKS - 1 }, changes };
}

/** Medal for a run: gold when it wins changing few enough switches. */
export const cartMedal = (level: MinecartLevel, r: CartRun) =>
  r.outcome !== 'win' ? null : r.changes <= level.goldChanges ? 'gold' : 'solved';
